import { createStyles, makeStyles } from "@material-ui/core";
import cls from "classnames";
import * as React from "react";

import { RelodeTheme } from "../";

const useLayoutStyles = makeStyles<RelodeTheme, LayoutProps>(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "grid",
                height: "fit-content",
                width: "100%",
                gridColumnGap: theme.spacing(3),
                gridRowGap: ({ gapSize }) => {
                    switch (gapSize) {
                        case "small":
                            return theme.spacing(1);
                        case "large":
                            return theme.spacing(8);
                        case "medium":
                        default:
                            return theme.spacing(3);
                    }
                },
            },
            100: {
                gridTemplateColumns: "1fr",
                [theme.breakpoints.down("sm")]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                },
            },
            "50/50": {
                gridTemplateColumns: "1fr 1fr",
                [theme.breakpoints.down("sm")]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                },
            },
            "40/60": {
                gridTemplateColumns: "40% 1fr",
                [theme.breakpoints.down("sm")]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                },
            },
            "25/25/50": {
                gridTemplateColumns: "1fr 1fr 2fr",
                [theme.breakpoints.down("sm")]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                },
            },
            "50/25/25": {
                gridTemplateColumns: "2fr 25% 25%",
                [theme.breakpoints.down("sm")]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                },
            },
            "75/25": {
                gridTemplateColumns: "75% 1fr",
                [theme.breakpoints.down("sm")]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                },
            },
            "60/20/20": {
                gridTemplateColumns: "60% 20% 20%",
                [theme.breakpoints.down("sm")]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                },
            },
            "25/25/25/25": {
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                [theme.breakpoints.down("sm")]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                },
            },
            teamPlansCustom: {
                gridTemplateColumns: "16% 20% 10% 10%",
                [theme.breakpoints.down("lg")]: {
                    gridTemplateColumns: "18% 28% 12% 12%",
                },
                [theme.breakpoints.down("md")]: {
                    gridTemplateColumns: "18% 36% 15% 15%",
                },
                [theme.breakpoints.down("sm")]: {
                    gridTemplateColumns: "minmax(0, 1fr)",
                },
            },
        }),
    { name: "Layout" }
);

export type LayoutProps = {
    children: React.ReactNode;
    /**
     * - 100 = single full width column
     * - 50/50 = two columns of equal width
     * - 25/25/50 = three columns.  First two are equal and together take up the first half.
     * The third column takes the second half
     */
    mode:
        | "100"
        | "50/50"
        | "50/25/25"
        | "40/60"
        | "25/25/50"
        | "25/25/25/25"
        | "60/20/20"
        | "75/25"
        | "teamPlansCustom";
    /**
     * @default [true]
     *
     * @description
     * - true = grid gap will be compressed
     * - false = grid gap will be normal
     */
    gapSize?: "small" | "medium" | "large";
    className?: string;
};

/**
 * Used to layout components in one of three column configurations. Internally uses a grid to perform layout.
 * * Internally uses a simple grid to perform layout.
 * * not appropriate for complex responsiveness
 * * can be nested for more complex layouts
 * @param mode (*required*) one of three column layout modes
 * @param children (*required*) components to layout
 * @param gapSize (*optional*) small/medium/large grid gap
 * @see
 * `<LayoutCell>` for spanning rows or columns
 */
export const Layout = (props: LayoutProps) => {
    const {
        mode = "100",
        gapSize = "medium",
        className,
        ...passThrough
    } = props;
    const classes = useLayoutStyles(props);

    return (
        <div className={cls(classes.root, classes[mode], className)}>
            {props.children}
        </div>
    );
};
